import { useAccessibilityTTS, useDropdownTTS } from '@/hooks';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useMainFocusTrapControl } from '@/hooks/useMainFocusTrapControl';
import { PATHS } from '@/router/paths';
import { TrackingService } from '@/services/analytics/TrackingService';
import { useConfigStore, useFnbStore, useHomeStore, useShopStore, type languageType } from '@/stores';
import { CONFIG_CONSTANTS, buildMenuFromBuildingMenus, getRequestStaffHelpFromBuildingMenus } from '@/stores/configStore';
import type { GNBItemType } from '@/types';
import { AppUtils } from '@/utils/app';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

type UseLanguageDropdownOptions = {
    onBarrierFreeUsed?: () => void;
    onZoomButtonsOpen?: (isOpen: boolean) => void;
};

export const useLanguageDropdown = ({ onBarrierFreeUsed, onZoomButtonsOpen }: UseLanguageDropdownOptions = {}) => {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();

    const ui = useConfigStore((state) => state.config.ui);
    const voice = useConfigStore((state) => state.config.tts.voice);
    const language = useConfigStore((state) => state.config.ui.language);
    const buildingMenus = useConfigStore((state) => state.config.server?.buildingMenus);
    const storeLangs = useConfigStore((state) => state.config?.server?.store?.storeLangs);
    const isScreenSaverFullscreen = useConfigStore((s) => s.config?.ui?.isScreenSaverFullscreen);
    const updateConfig = useConfigStore((state) => state.updateConfig);
    const setFnbKeyboardLanguage = useFnbStore((state) => state.setKeyboardLanguage);
    const setShopKeyboardLanguage = useShopStore((state) => state.setKeyboardLanguage);

    const { trapFocus, releaseFocus } = useFocusTrap();
    const { disableMainFocusTrap } = useMainFocusTrapControl();
    const { speakOnFocus } = useDropdownTTS();
    const { speak } = useAccessibilityTTS();
    const isHomeMenuVisible = useHomeStore((s) => s.isHomeMenuVisible);

    const [isOpen, setIsOpen] = useState(false);

    const dropdownRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const speakTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const configTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const suppressSpeakOnFocusRef = useRef(false);

    const activeUiLang = i18n.language as languageType;

    const languageButtonLabel = useMemo(() => {
        const entry = storeLangs?.find((lang) => lang.code === language);
        const rawName = entry?.display_name?.trim();
        return rawName || AppUtils.getLanguageLabelByActiveLang(language, activeUiLang);
    }, [storeLangs, language, activeUiLang]);

    const resolveLanguageOptionLabel = useCallback(
        (option: { label: string; value: languageType }) => {
            const entry = storeLangs?.find((lang) => lang.code === option.value);
            const rawName = entry?.display_name?.trim();
            return rawName || AppUtils.getLanguageLabelByActiveLang(option.value, activeUiLang);
        },
        [storeLangs, activeUiLang]
    );

    const handleConfigUpdate = useCallback(
        (updates: Partial<typeof ui>) => {
            updateConfig({ ui: { ...ui, ...updates } });
        },
        [ui, updateConfig]
    );

    const speakLanguageSelection = useCallback(
        (languageLabel: string, lang: languageType) => {
            if (!voice) return;
            const ttsText = i18n.t('footer.barrier_free.language_selected', { language: languageLabel, lng: lang });
            speak(ttsText, lang);
        },
        [voice, speak, i18n]
    );

    const handleTriggerFocus = useCallback(
        (target: EventTarget) => {
            if (suppressSpeakOnFocusRef.current) return;
            speakOnFocus(target);
        },
        [speakOnFocus]
    );

    const restoreFocusToTrigger = useCallback(() => {
        suppressSpeakOnFocusRef.current = true;
        buttonRef.current?.focus({ preventScroll: true });
        suppressSpeakOnFocusRef.current = false;
    }, []);

    const closeDropdown = useCallback(() => {
        setIsOpen(false);
        releaseFocus();
    }, [releaseFocus]);

    const handleLanguageSelect = useCallback(
        (langValue: string, optionLabel: string) => {
            if (speakTimeoutRef.current) {
                clearTimeout(speakTimeoutRef.current);
            }
            if (configTimeoutRef.current) {
                clearTimeout(configTimeoutRef.current);
            }

            const mainLanguage = (storeLangs?.find((lang) => lang.main_lang)?.code as languageType) || CONFIG_CONSTANTS.DEFAULT_LANGUAGE;
            const menu: GNBItemType[] = buildMenuFromBuildingMenus(buildingMenus, langValue, mainLanguage);

            handleConfigUpdate({
                language: langValue as languageType,
                menu,
                requestStaffHelp: getRequestStaffHelpFromBuildingMenus(buildingMenus, langValue),
            });

            i18n.changeLanguage(langValue);
            onBarrierFreeUsed?.();
            onZoomButtonsOpen?.(false);
            TrackingService.onLocaleChanged();
            setIsOpen(false);
            releaseFocus();
            restoreFocusToTrigger();

            if (langValue === 'ko') {
                setFnbKeyboardLanguage('ko');
                setShopKeyboardLanguage('ko');
            } else {
                setFnbKeyboardLanguage('en');
                setShopKeyboardLanguage('en');
            }

            speakTimeoutRef.current = setTimeout(() => {
                speakLanguageSelection(optionLabel, langValue as languageType);
            }, 50);

            configTimeoutRef.current = setTimeout(() => {
                updateConfig({
                    ui: {
                        zoomIn: false,
                        zoomClosed: true,
                        contrast: false,
                        screenMode: 'fullscreen',
                        volume: 3,
                        zoomLevel: 1,
                    },
                });

                if (isScreenSaverFullscreen && !isHomeMenuVisible) {
                    navigate(PATHS.FLOOR_GUIDE.INDEX);
                } else {
                    navigate(PATHS.HOME.INDEX);
                }
            }, 1200);
        },
        [
            buildingMenus,
            storeLangs,
            handleConfigUpdate,
            updateConfig,
            navigate,
            speakLanguageSelection,
            onZoomButtonsOpen,
            onBarrierFreeUsed,
            i18n,
            isScreenSaverFullscreen,
            isHomeMenuVisible,
            setFnbKeyboardLanguage,
            setShopKeyboardLanguage,
            releaseFocus,
            restoreFocusToTrigger,
        ]
    );

    const handleToggleDropdown = useCallback(() => {
        setIsOpen((prev) => !prev);
    }, []);

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (!isOpen) {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setIsOpen(true);
                }
                return;
            }

            if (event.key === 'Escape') {
                event.preventDefault();
                closeDropdown();
            }
        },
        [isOpen, closeDropdown]
    );

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                closeDropdown();
            }
        };

        const handleEscapeKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isOpen) {
                closeDropdown();
            }
        };

        if (isOpen) {
            const firstOptionButton = dropdownRef.current?.querySelector('button[role="button"]') as HTMLButtonElement;
            if (firstOptionButton) {
                firstOptionButton.focus();
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscapeKey);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, [isOpen, closeDropdown]);

    useEffect(() => {
        if (isOpen && dropdownRef.current) {
            disableMainFocusTrap();
            trapFocus(dropdownRef.current, isOpen);
        } else {
            releaseFocus();
        }
    }, [isOpen, trapFocus, releaseFocus, disableMainFocusTrap]);

    useEffect(() => {
        return () => {
            if (speakTimeoutRef.current) {
                clearTimeout(speakTimeoutRef.current);
            }
            if (configTimeoutRef.current) {
                clearTimeout(configTimeoutRef.current);
            }
        };
    }, []);

    return {
        ui,
        isOpen,
        dropdownRef,
        buttonRef,
        languageButtonLabel,
        resolveLanguageOptionLabel,
        handleLanguageSelect,
        handleToggleDropdown,
        handleKeyDown,
        speakOnFocus,
        handleTriggerFocus,
        closeDropdown,
        t,
    };
};
